'use client'

import { Icon } from '@components/primitives/Icon'
import { getColorConfig } from '@components/primitives/ToggleButton/getColorConfig'
import { classMerge } from '@components/utilities/classMerge'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { useState } from 'react'

export const CheckboxMultiSelect = ({
  className,
  defaultValues,
  formRef,
  name,
  onChange,
  options,
  required = false,
}: {
  className?: string
  defaultValues: string[]
  formRef?: React.MutableRefObject<HTMLFormElement | null>
  name?: string
  onChange: (values: string[]) => void
  options: { icon?: IconDefinition; label: string; value: string }[]
  required?: boolean
}) => {
  const [selectedValues, setSelectedValues] = useState(defaultValues)

  const optionClicked = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value]

    setSelectedValues(newSelectedValues)
    onChange(newSelectedValues)
    // trigger a form input event to update form state
    setTimeout(() => formRef?.current?.dispatchEvent(new Event('input')))
  }

  const {
    backgroundColor,
    selectedBorderColor,
    selectedTextColor,
    unselectedTextColor,
  } = getColorConfig('success')

  return (
    <>
      {options.map(({ icon, label, value }) => (
        <div
          className={classMerge(
            `flex cursor-pointer items-center justify-center rounded-lg border-2 border-neutral200 p-3 hover:bg-neutral100 ${
              selectedValues.includes(value)
                ? `${selectedBorderColor} ${backgroundColor}`
                : ''
            } ${selectedValues.includes(value) ? selectedTextColor : unselectedTextColor}`,
            className,
          )}
          key={value}
          onClick={() => {
            optionClicked(value)
          }}
          onKeyDown={(e) => {
            // select with spacebar
            if (e.key === ' ') {
              optionClicked(value)
            }
          }}
          tabIndex={0}
        >
          <input
            checked={selectedValues.includes(value)}
            className='absolute opacity-0'
            id={value}
            name={name}
            onChange={() => {}} // handled by onclick in parent, but required to avoid React warning
            required={required && selectedValues.length === 0} // Only required if nothing is selected
            tabIndex={-1}
            type='checkbox'
            value={value}
          />
          <label
            className='-ml-3 cursor-pointer text-sm font-bold' // Adjust as needed
            onClick={(e) => {
              optionClicked(value)
              e.preventDefault()
            }}
          >
            {icon && <Icon className='mr-2' icon={icon} />}
            {label}
          </label>
        </div>
      ))}
    </>
  )
}
